import React, { useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useApolloClient } from "@apollo/client";
import { Select } from "@outpave/ds";
import type { SelectOption } from "@outpave/ds";
import { DEFAULT_PAGINATION_SIZE } from "components/common/Constants";
import UserStatus from "enums/UserStatus";
import { USER_OPTIONS } from "graphql/user";
import { useSelectSearch } from "hooks/useSelectSearch";
import type { SelectSearchPage } from "hooks/useSelectSearch";
import { AssignedCardUser } from "./types";

interface UserOptionNode {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
}

interface UserOptionsData {
  users: {
    edges: { node: UserOptionNode }[] | null;
    pageInfo: { hasNextPage: boolean; endCursor: string | null } | null;
  } | null;
}

/** "Name - email", the format the design and the legacy employee picker share. */
export const formatUserOption = (user: AssignedCardUser): SelectOption => ({
  id: user.id,
  label: user.email ? `${user.name} - ${user.email}` : user.name,
});

export interface AssignedUserSelectProps {
  value: AssignedCardUser | null;
  onChange: (user: AssignedCardUser) => void;
  /** Momentarily inert, e.g. while a save is in flight. Keeps the loaded list. */
  disabled?: boolean;
  /** Nothing can be assigned: show the current user without querying the directory. */
  readOnly?: boolean;
}

/** Picks the Outpave user an imported card's spend belongs to. */
const AssignedUserSelect: React.FC<AssignedUserSelectProps> = ({
  value,
  onChange,
  disabled = false,
  readOnly = false,
}) => {
  const { t } = useTranslation();
  const client = useApolloClient();
  // Select hands back only an id, so the rest of the user is kept here.
  const usersById = useRef(new Map<string, AssignedCardUser>());
  if (value) usersById.current.set(value.id, value);

  const seed = useMemo(() => (value ? formatUserOption(value) : null), [value]);

  const fetchPage = useCallback(
    async (query: string, after: string | null): Promise<SelectSearchPage> => {
      const { data } = await client.query<UserOptionsData>({
        query: USER_OPTIONS,
        variables: {
          first: DEFAULT_PAGINATION_SIZE,
          after: after ?? undefined,
          status: [UserStatus.ACTIVE, UserStatus.PENDING],
          searchTerm: query || undefined,
        },
        fetchPolicy: "network-only",
      });

      const users: AssignedCardUser[] = (data?.users?.edges ?? []).map(
        ({ node }) => ({
          id: node.id,
          name: [node.firstName, node.lastName].filter(Boolean).join(" "),
          email: node.email,
        }),
      );
      users.forEach((user) => usersById.current.set(user.id, user));

      return {
        options: users.map(formatUserOption),
        hasNextPage: data?.users?.pageInfo?.hasNextPage ?? false,
        endCursor: data?.users?.pageInfo?.endCursor ?? null,
      };
    },
    [client],
  );

  // The seed keeps a current user outside the first page from rendering as a blank field.
  const search = useSelectSearch({ fetchPage, seed, skip: readOnly });

  return (
    <Select
      label={t("Assigned user")}
      placeholder={t("Select a user")}
      value={value?.id ?? null}
      clearable={false}
      fullWidth
      {...(readOnly
        ? { options: seed ? [seed] : [], disabled: true }
        : {
            searchable: true,
            options: search.options,
            loading: search.loading,
            highlight: search.query,
            onInputChange: search.onInputChange,
            onScrollEnd: search.onScrollEnd,
            noOptionsText: t("No users found"),
            disabled,
          })}
      onChange={(id) => {
        const user = id ? usersById.current.get(id) : undefined;
        // Clearing is ignored: nothing can unassign a card. See README.md.
        if (user) onChange(user);
      }}
    />
  );
};

export default AssignedUserSelect;
